import React, { forwardRef, Ref, useMemo, useState } from 'react';
import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';
import { Button as ButtonOriginal } from 'primereact/button';
import { Avatar, AvatarProps, AvatarSize } from '../Avatar';
import './AvatarSet.scss';
import { Button } from '../../_common/Button';
import BusinessCard from '../../_common/panel/BusinessCard';
import { useCounterpartApplication } from '../../../contexts/useCounterpartApplication';
import { Spinner } from '../Spinner';
import { Dialog } from '../Dialog';

type AvatarSetAvatarProp = Omit<AvatarProps, 'size'>;

export type AvatarSetProps = {
	avatars?: AvatarSetAvatarProp[];
	size?: AvatarSize;
	className?: string;
	interactive?: boolean;
};

export const AvatarSet = forwardRef(
	({ avatars, size = 'md', className, interactive = false }: AvatarSetProps, ref: Ref<ButtonOriginal>) => {
		const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
		const { t } = useTranslation();
		const { data: counterpartData, isLoading } = useCounterpartApplication(!interactive);

		const openModal = () => {
			setShowProfileModal(true);
		};

		const finalAvatars = useMemo(
			() => (avatars || []).map((avatarProps, index) => ({ key: index, ...avatarProps })),
			[avatars]
		);

		const renderDialogContent = () => {
			if (isLoading) {
				return <Spinner center />;
			}

			if (!counterpartData) {
				return <div>Failed to load counterpart application details.</div>;
			}

			return <BusinessCard data={counterpartData} disableMessage />;
		};

		return (
			<>
				{interactive && (
					<Dialog
						visible={showProfileModal}
						onHide={() => {
							setShowProfileModal(false);
						}}
						className="p-dialog-buissnescard"
						title={t('userPanel.pairing.personalDetailsPair', {
							type: counterpartData?.applicationRole === 'mentee' ? t('misc.mentees') : t('misc.mentors'),
						})}
						size="lg"
					>
						{renderDialogContent()}
					</Dialog>
				)}
				<Button
					ref={ref}
					onClick={() => {
						if (interactive) {
							openModal();
						}
					}}
					style={{ padding: 0, margin: 0, border: 'none', background: 'transparent' }}
				>
					<div className={classNames('avatar-set', `avatar-set-size-${size}`, className)}>
						{finalAvatars.map(({ key, ...avatarProps }, index) => (
							<div key={key} className="avatar-set-node">
								<Avatar size={size} style={{ zIndex: index }} {...avatarProps} />
							</div>
						))}
					</div>
				</Button>
			</>
		);
	}
);
